import { t } from './i18n';

interface ConventionField {
  name: string;
  label: string;
  type: string;
  required?: boolean;
}

interface ConventionStructure {
  fields: ConventionField[];
}

interface Validation {
  validatorName: string;
  timestamp: string;
}

interface Convention {
  fullname?: string;
  username: string;
  email?: string;
  student_number?: string;
  additional_info?: {
    healthInsurance?: string;
  };
  promo_id: number;
  year: number;
  info?: {
    [key: string]: any;
    validations?: Validation[];
  };
}

interface Promo {
  id: number;
  name: string;
}

export function printConvention(
  convention: Convention,
  conventionStructure: ConventionStructure | null,
  promos: Promo[]
) {
  // Create a new window for printing
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    alert(t("Please allow pop-ups to print the convention."));
    return;
  }

  // Build the HTML content
  const studentName = convention.fullname || convention.username;

  // Get promo name
  const promo = promos.find(p => p.id === convention.promo_id);
  const promoName = promo?.name || t("Not specified");

  // Build student info block
  const studentInfoHtml = `
    <div class="section-box">
      <div class="section-header">
          <h3>${t("Student Information")}</h3>
      </div>
      <div class="section-content">
        <table>
          <tr>
            <td class="label">${t("Class")}:</td>
            <td class="value">${promoName}</td>
          </tr>
          <tr>
            <td class="label">${t("Full Name")}:</td>
            <td class="value">${studentName}</td>
          </tr>
          <tr>
            <td class="label">${t("Username")}:</td>
            <td class="value">${convention.username}</td>
          </tr>
          ${convention.student_number ? `
          <tr>
            <td class="label">${t("Student Number")}:</td>
            <td class="value">${convention.student_number}</td>
          </tr>
          ` : ''}
          ${convention.email ? `
          <tr>
            <td class="label">${t("Email")}:</td>
            <td class="value">${convention.email}</td>
          </tr>
          ` : ''}
          ${convention.additional_info?.healthInsurance ? `
          <tr>
            <td class="label">${t("Health Insurance - City + Region")}:</td>
            <td class="value">${convention.additional_info.healthInsurance}</td>
          </tr>
          ` : ''}
        </table>
      </div>
    </div>
  `;

  // Build fields HTML with group support
  let fieldsHtml = '';
  let inGroup = false;
  let groupHtml = '';
  let groupTitle = '';
  let ungroupedFieldsHtml = '';

  if (conventionStructure && conventionStructure.fields) {
    conventionStructure.fields.forEach(field => {
      // Skip title field
      if (field.name === 'title') return;

      if (field.type === 'group') {
        // Add any accumulated ungrouped fields first
        if (ungroupedFieldsHtml) {
          fieldsHtml += `
          <div class="section-ungrouped">
            <div class="section-content">
              <table>
                ${ungroupedFieldsHtml}
              </table>
            </div>
            </div>
          </div>
          `;
          ungroupedFieldsHtml = '';
        }
        // Start a new group
        inGroup = true;
        groupTitle = field.label;
        groupHtml = '';
      } else if (field.type === 'group-end') {
        // End the current group
        if (inGroup) {
          fieldsHtml += `
            <div class="section-box">
              <div class="section-header">
                  <h4>${groupTitle}</h4>
              </div>
              <div class="section-content">
                <table>
                  ${groupHtml}
                </table>
              </div>
            </div>
          `;
        }
        inGroup = false;
        groupHtml = '';
      } else {
        // Regular field
        const value = convention.info?.[field.name] || t("Not specified");
        const fieldRow = `
          <tr>
            <td class="label">${field.label}${field.required ? '*' : ''}:</td>
            <td class="value">${value}</td>
          </tr>
        `;

        if (inGroup) {
          groupHtml += fieldRow;
        } else {
          ungroupedFieldsHtml += fieldRow;
        }
      }
    });

    // Close any unclosed group
    if (inGroup && groupHtml) {
      fieldsHtml += `
        <div class="section-box">
            <div class="section-header">
                <h4>${groupTitle}</h4>
            </div>
            <div class="section-content">
            <table>
              ${groupHtml}
            </table>
          </div>
        </div>
      `;
    }

    // Add any remaining ungrouped fields
    if (ungroupedFieldsHtml) {
      fieldsHtml += `
        <div class="section-ungrouped">
          <div class="section-content">
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
              ${ungroupedFieldsHtml}
            </table>
          </div>
        </div>
      `;
    }
  }

  // Build validators HTML
  let validatorsHtml = '';
  if (convention.info?.validations && convention.info.validations.length > 0) {
    convention.info.validations.forEach(validation => {
      validatorsHtml += `
        <tr>
          <td style="padding: 6px 8px; border: 1px solid #666;">${validation.validatorName}</td>
          <td style="padding: 6px 8px; border: 1px solid #666;">${new Date(validation.timestamp).toLocaleString()}</td>
        </tr>
      `;
    });
  }

  const html = `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="utf-8">
    <title>${t("Internship Agreement")} - ${studentName} (${convention.year})</title>
    <style>
        /* --- GENERAL STYLES --- */
        body {
            font-family: "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
            font-size: 11pt; /* Taille standard impression */
            line-height: 1.5;
            color: #333;
            margin: 0;
            background-color: #f9f9f9; /* Fond gris clair à l'écran seulement */
        }

        /* Conteneur principal pour simuler la feuille A4 à l'écran */
        .page-container {
            max-width: 21cm;
            margin: 40px auto;
            background: white;
            padding: 2cm;
            box-shadow: 0 0 15px rgba(0,0,0,0.1);
            min-height: 29.7cm;
        }

        /* --- TYPOGRAPHY --- */
        h1 {
            font-family: Georgia, "Times New Roman", serif;
            text-transform: uppercase;
            font-size: 20pt;
            text-align: center;
            border-bottom: 2px solid #333;
            padding-bottom: 15px;
            margin-bottom: 30px;
            letter-spacing: 1px;
        }

        h2 {
            font-size: 16pt;
            border-bottom: 1px solid #ddd;
            padding-bottom: 5px;
            margin-top: 30px;
            color: #000;
        }

        h3, h4 {
            margin: 0;
            font-size: 12pt;
            font-weight: 700;
            text-transform: uppercase;
            color: #444;
        }

        /* --- SECTIONS & BOXES --- */
        .section-ungrouped {
            margin-bottom: 25px;
            background-color: #fff;
            page-break-inside: avoid; /* Évite de couper le bloc à l'impression */
        }
        .section-box {
            border: 1px solid #ddd; /* Bordure fine et discrète */
            margin-bottom: 25px;
            background-color: #fff;
            page-break-inside: avoid; /* Évite de couper le bloc à l'impression */
        }

        .section-header {
            background-color: #f0f0f0; /* Fond gris clair pour le titre du bloc */
            padding: 8px 15px;
            border-bottom: 1px solid #ddd;
        }

        .section-content {
            padding: 10px 15px;
        }

        /* --- TABLES --- */
        table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 10px;
        }

        /* Colonne des étiquettes (Labels) */
        td.label {
            width: 35%;
            font-weight: 600;
            color: #555;
            padding: 6px 0;
            vertical-align: top;
            border-bottom: 1px dotted #ccc; /* Ligne pointillée discrète */
        }

        /* Colonne des valeurs */
        td.value {
            padding: 6px 0 6px 15px;
            color: #000;
            border-bottom: 1px dotted #ccc;
        }

        /* Supprimer la bordure du bas pour la dernière ligne */
        tr:last-child td {
            border-bottom: none;
        }

        /* Tableau de validation spécifique */
        .validation-table th {
            background-color: #eee;
            color: #000;
            padding: 8px;
            text-align: left;
            font-size: 10pt;
            border: 1px solid #333;
        }

        .validation-table td {
            border: 1px solid #ccc;
            padding: 8px;
        }

        /* --- UTILS --- */
        .text-pre {
            white-space: pre-wrap; /* Garde les retours à la ligne */
        }

        /* --- BUTTONS (SCREEN ONLY) --- */
        .actions {
            text-align: center;
            padding: 20px;
            background: #eee;
            border-top: 1px solid #ccc;
            position: fixed;
            bottom: 0;
            left: 0;
            width: 100%;
        }
        
        .btn {
            padding: 10px 20px;
            font-size: 14px;
            cursor: pointer;
            border: none;
            border-radius: 4px;
            font-weight: bold;
        }
        
        .btn-print { background-color: #333; color: white; }
        .btn-close { background-color: #999; color: white; margin-left: 10px; }

        /* --- PRINT SETTINGS (CRITICAL) --- */
        @media print {
            body {
                background: none;
                font-size: 11pt;
            }
            .page-container {
                box-shadow: none;
                margin: 0;
                padding: 0;
                width: 100%;
                max-width: none;
            }
            .actions { display: none !important; }
            
            /* Force les couleurs exactes (pour que les gris de fond s'impriment) */
            * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            
            @page {
                margin: 1.5cm; /* Marges saines pour l'impression */
                size: A4;
            }
        }
    </style>
</head>
<body>
  <div class="page-container">
    <h1>${t("Internship Agreement")} - ${t("Academic Year")} ${convention.year}</h1>
    
    ${studentInfoHtml}
    
    ${fieldsHtml}
    
    ${validatorsHtml ? `
      <!-- Bloc Signatures / Validations -->
        <div style="margin-top: 40px; page-break-inside: avoid;">
          <h2>${t("Validations")}</h2>
          <table class="validation-table">
            <thead>
              <tr>
                <th style="width: 60%">${t("Validator")}</th>
                <th style="width: 40%">${t("Date")}</th>
              </tr>
            </thead>
            <tbody>
              ${validatorsHtml}
            </tbody>
          </table>
        </div>
    ` : ''}
    
      <div style="margin-top: 40px; text-align: center; page-break-inside: avoid;">
        <button onclick="window.print()" style="padding: 10px 20px; font-size: 14px; cursor: pointer; background-color: #333; color: white; border: none; border-radius: 3px; margin-right: 10px;">
          ${t("Print")}
        </button>
        <button onclick="window.close()" style="padding: 10px 20px; font-size: 14px; cursor: pointer; background-color: #666; color: white; border: none; border-radius: 3px;">
          ${t("Close")}
        </button>
      </div>
    </div>
  </body>
</html>
  `;

  printWindow.document.write(html);
  printWindow.document.close();
}
